import React, { useState, useEffect } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { navDelay, loaderDelay } from '@utils';
import { usePrefersReducedMotion } from '@hooks';

const StyledHeroSection = styled.section`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  align-items: flex-start;
  min-height: 80vh;
  height: 80vh;
  padding: 200px 0px 0px 0px;

  @media (max-height: 700px) and (min-width: 700px), (max-width: 360px) {
    height: auto;
    padding-top: var(--nav-height);
  }

  h1 {
    margin: 0 0 30px 4px;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: clamp(var(--fz-sm), 5vw, var(--fz-md));
    font-weight: 400;

    @media (max-width: 480px) {
      margin: 0 0 20px 2px;
    }
  }

  h3 {
    margin-top: 5px;
    color: var(--slate);
    line-height: 0.9;
  }

  p {
    margin: 20px 0 0;
    max-width: 540px;
  }

  .email-link {
    ${({ theme }) => theme.mixins.bigButton};
    margin-top: 50px;
  }
`;

const Hero = () => {
  const [isMounted, setIsMounted] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  const data = useStaticQuery(graphql`
    query HeroContentQuery {
      hero: allMarkdownRemark(filter: { fileAbsolutePath: { regex: "/content/home/" } }, limit: 1) {
        edges {
          node {
            frontmatter {
              greeting
              name
              headline
              ctaText
              ctaUrl
            }
            rawMarkdownBody
          }
        }
      }
    }
  `);

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => setIsMounted(true), navDelay);
    return () => clearTimeout(timeout);
  }, []);

  const calculateExperience = () => {
    const startDate = new Date('2018-06-01');
    const currentDate = new Date();
    const diffInMs = currentDate.getTime() - startDate.getTime();
    const yearsOfExperience = diffInMs / (1000 * 60 * 60 * 24 * 365);
    return Math.floor(yearsOfExperience + 0.5);
  };

  const experience = calculateExperience();

  const fm = data.hero.edges[0]?.node.frontmatter || {};
  const body = data.hero.edges[0]?.node.rawMarkdownBody || '';

  const one = <h1>{fm.greeting || 'Hi, my name is'}</h1>;
  const two = <h2 className="big-heading">{fm.name || 'Your Name.'}</h2>;
  const three = <h3 className="big-heading">{fm.headline || 'I build things for the web.'}</h3>;

  const four = (
    <>
      <p>{(body || '').replace('{{years}}', String(experience))}</p>
    </>
  );
  const five = (
    <a className="email-link" href={fm.ctaUrl || '#'} target="_blank" rel="noreferrer">
      {fm.ctaText || 'Get In Touch'}
    </a>
  );

  const items = [one, two, three, four, five];

  return (
    <StyledHeroSection>
      {prefersReducedMotion ? (
        <>
          {items.map((item, i) => (
            <div key={i}>{item}</div>
          ))}
        </>
      ) : (
        <TransitionGroup component={null}>
          {isMounted &&
            items.map((item, i) => (
              <CSSTransition key={i} classNames="fadeup" timeout={loaderDelay}>
                <div style={{ transitionDelay: `${i + 1}00ms` }}>{item}</div>
              </CSSTransition>
            ))}
        </TransitionGroup>
      )}
    </StyledHeroSection>
  );
};

export default Hero;
